import { MESApiService, type spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type {
	BilletWorkOrderRow,
	BilletWorkOrdersPickerBilletSize,
	BilletWorkOrdersPickerCrew,
	BilletWorkOrdersPickerProductSize,
	BilletWorkOrdersPickerProductType,
	BilletWorkOrdersPickerWorkOrder,
	BilletWorkOrdersPickers,
} from "../models/BilletWorkOrders";

export interface GetBilletWorkOrdersParams {
	start: string;
	end: string;
	workOrderId?: number | null;
	billetSizeId?: number | null;
	crewId?: string | null;
	productSizeId?: number | null;
	productTypeId?: number | null;
	signal?: AbortSignal;
}

export const getBilletWorkOrders = async ({
	start,
	end,
	workOrderId = null,
	billetSizeId = null,
	crewId = null,
	productSizeId = null,
	productTypeId = null,
	signal,
}: GetBilletWorkOrdersParams): Promise<BilletWorkOrderRow[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "start", value: start },
		{ name: "end", value: end },
		{ name: "workOrderId", value: workOrderId },
		{ name: "billetSizeId", value: billetSizeId },
		{ name: "crewId", value: crewId },
		{ name: "productSizeId", value: productSizeId },
		{ name: "productTypeId", value: productTypeId },
	];

	const spName = "[MILL].[GetBilletWorkOrdersDataWeb]";
	const resp = await apiService.callV2(spName, parameters, signal);

	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching billet work orders");
	}
	return get(resp, "data.tables[0].rows", []) as BilletWorkOrderRow[];
};

export interface GetBilletWorkOrdersPickersParams {
	start: string;
	end: string;
	signal?: AbortSignal;
}

export const getBilletWorkOrdersPickers = async ({
	start,
	end,
	signal,
}: GetBilletWorkOrdersPickersParams): Promise<BilletWorkOrdersPickers> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "start", value: start },
		{ name: "end", value: end },
		{ name: "timeZoneCode", value: null },
		{ name: "onlyActives", value: true },
	];

	const spName = "[MILL].[GetBilletWorkOrdersPickersDataWeb]";
	const resp = await apiService.callV2(spName, parameters, signal);

	if (!resp.ok) {
		throw new Error(
			resp.message || "Error fetching billet work orders pickers",
		);
	}

	const billetSizes = get(
		resp,
		"data.tables[1].rows",
		[],
	) as BilletWorkOrdersPickerBilletSize[];
	const crews = get(
		resp,
		"data.tables[2].rows",
		[],
	) as BilletWorkOrdersPickerCrew[];
	const productSizes = get(
		resp,
		"data.tables[3].rows",
		[],
	) as BilletWorkOrdersPickerProductSize[];
	const productTypes = get(
		resp,
		"data.tables[4].rows",
		[],
	) as BilletWorkOrdersPickerProductType[];
	const workOrders = get(
		resp,
		"data.tables[5].rows",
		[],
	) as BilletWorkOrdersPickerWorkOrder[];

	return { billetSizes, crews, productSizes, productTypes, workOrders };
};

export interface UpsertBilletWorkOrderParams {
	Id: number;
	WorkOrderId: number | null;
}

export const upsertBilletWorkOrder = async ({
	Id,
	WorkOrderId,
}: UpsertBilletWorkOrderParams): Promise<void> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "Id", value: Id },
		{ name: "WorkOrderId", value: WorkOrderId },
	];

	const resp = await apiService.callV2(
		"[MILL].[UpsertBilletWorkOrder]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error updating billet work order");
	}
};
